/*
 * newsbot - API endpoint display with copy button
 */

import { useState } from "react";
import { DocumentDuplicateIcon, CheckIcon, LinkIcon } from "@heroicons/react/24/outline";
import { classNames } from "@utils";

interface ApiEndpointDisplayProps {
  slug: string;
}

export function ApiEndpointDisplay({ slug }: ApiEndpointDisplayProps) {
  const [copied, setCopied] = useState(false);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const endpoint = `${baseUrl}/api/v1/export/top-stories?category=${slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(endpoint);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
      <div className="flex items-start gap-3">
        <LinkIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
            API Export Endpoint
          </h4>
          <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
            Use this URL to fetch stories matching this filter via HTTP request.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded text-xs text-gray-800 dark:text-gray-200 truncate">
              {endpoint}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className={classNames(
                "flex items-center gap-1 px-2 py-1.5 text-xs rounded transition shrink-0",
                copied
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                  : "bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-gray-600"
              )}
            >
              {copied ? (
                <>
                  <CheckIcon className="w-3.5 h-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                  Copy
                </>
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
            Use a logged-in Authorization header or create an X-API-Key integration token in Account Settings. {" "}
            Optional params: <code className="px-1 bg-white dark:bg-gray-800 rounded">limit=10</code>, <code className="px-1 bg-white dark:bg-gray-800 rounded">since_hours=24</code>, <code className="px-1 bg-white dark:bg-gray-800 rounded">min_importance=0.5</code>
          </p>
        </div>
      </div>
    </div>
  );
}
